
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Phone, Mail, Globe, MapPin, MessageSquare, User, Building, Clock, Edit } from "lucide-react";
import { OutreachContact } from "@/types/outreach";
import { format } from "date-fns";
import { useState } from "react";
import { EditOutreachContactDialog } from "./EditOutreachContactDialog";

interface OutreachContactDetailModalProps {
  contact: OutreachContact | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const OutreachContactDetailModal = ({ contact, open, onOpenChange }: OutreachContactDetailModalProps) => {
  const [showEditDialog, setShowEditDialog] = useState(false);

  if (!contact) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_contacted':
        return 'bg-gray-100 text-gray-800';
      case 'contacted':
        return 'bg-blue-100 text-blue-800';
      case 'follow_up_needed':
        return 'bg-yellow-100 text-yellow-800';
      case 'interested':
        return 'bg-green-100 text-green-800';
      case 'not_interested':
        return 'bg-red-100 text-red-800';
      case 'signed_up':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'not_contacted':
        return 'Not Contacted';
      case 'contacted':
        return 'Contacted';
      case 'follow_up_needed':
        return 'Follow Up Needed';
      case 'interested':
        return 'Interested';
      case 'not_interested':
        return 'Not Interested';
      case 'signed_up':
        return 'Signed Up';
      default:
        return status.replace('_', ' ');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                {contact.business_name}
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEditDialog(true)}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status and Priority */}
            <div className="flex flex-wrap gap-3">
              <Badge className={getStatusColor(contact.status)}>
                {getStatusLabel(contact.status)}
              </Badge>
              <Badge variant="outline" className={getPriorityColor(contact.priority || 'medium')}>
                {contact.priority || 'medium'} priority
              </Badge>
              {contact.business_type && (
                <Badge variant="outline">
                  {contact.business_type.replace('_', ' ')}
                </Badge>
              )}
            </div>

            {/* Contact Person */}
            {contact.contact_person && (
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-gray-500" />
                <div>
                  <div className="font-medium">Contact Person</div>
                  <div className="text-gray-600">{contact.contact_person}</div>
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {contact.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-500">Email</div>
                      <div className="font-medium">{contact.email}</div>
                    </div>
                  </div>
                )}

                {contact.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-500">Phone</div>
                      <div className="font-medium">{contact.phone}</div>
                    </div>
                  </div>
                )}

                {contact.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-500">Website</div>
                      <div className="font-medium truncate">{contact.website}</div>
                    </div>
                  </div>
                )}

                {contact.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-500">Location</div>
                      <div className="font-medium">{contact.location}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Method and Source */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {contact.contact_method && (
                <div>
                  <div className="text-sm text-gray-500">Preferred Contact Method</div>
                  <div className="font-medium capitalize">{contact.contact_method.replace('_', ' ')}</div>
                </div>
              )}

              {contact.source && (
                <div>
                  <div className="text-sm text-gray-500">Source</div>
                  <div className="font-medium capitalize">{contact.source.replace('_', ' ')}</div>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg">Timeline</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-sm text-gray-500">Last Contact</div>
                    <div className="font-medium">
                      {format(new Date(contact.last_contact_date), 'MMM dd, yyyy')}
                    </div>
                  </div>
                </div>

                {contact.next_follow_up_date && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-orange-500" />
                    <div>
                      <div className="text-sm text-gray-500">Next Follow Up</div>
                      <div className="font-medium text-orange-600">
                        {format(new Date(contact.next_follow_up_date), 'MMM dd, yyyy')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {contact.notes && (
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Notes
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{contact.notes}</p>
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="border-t pt-4 text-sm text-gray-500 space-y-1">
              <div>Created: {format(new Date(contact.created_at), 'MMM dd, yyyy HH:mm')}</div>
              <div>Updated: {format(new Date(contact.updated_at), 'MMM dd, yyyy HH:mm')}</div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EditOutreachContactDialog
        contact={contact}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
      />
    </>
  );
};
